import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Component, Input, NgModule } from '@angular/core';
import * as i2$1 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import { of, fromEvent, BehaviorSubject } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import * as i2 from '@angular/forms';
import { FormsModule } from '@angular/forms';

/**
 * A function that loads the saved theme.
 * Defaults to loading from local storage with the key `theme`.
 */
const THEME_LOADER = new InjectionToken('', {
    factory() {
        return () => {
            if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
                return of(null);
            }
            return fromEvent(window, 'storage').pipe(filter(event => event.key === 'theme' && event.storageArea === localStorage), map(event => event.newValue), startWith(localStorage.getItem('theme')));
        };
    },
});
/**
 * A function that saves the theme.
 * Defaults to saving to local storage with the key `theme`.
 */
const THEME_SAVER = new InjectionToken('', {
    factory() {
        return theme => {
            if (typeof localStorage === 'undefined') {
                return;
            }
            if (theme) {
                localStorage.setItem('theme', theme);
            }
            else {
                localStorage.removeItem('theme');
            }
        };
    },
});
class ThemeService {
    constructor(document, loadHandler, saveHandler) {
        this._theme = new BehaviorSubject(null);
        (loadHandler()).subscribe(theme => this._theme.next(theme));
        this._theme.subscribe(theme => {
            if (theme) {
                document.body.setAttribute('data-theme', theme);
            }
            else {
                document.body.removeAttribute('data-theme');
            }
            saveHandler(theme);
        });
    }
    /**
     * @return a subject representing the currently active theme
     */
    get theme$() {
        return this._theme;
    }
    /**
     * @return `dark` if the user agent prefers dark color scheme, `light` otherwise
     */
    get detectedTheme() {
        return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    /**
     * @return an observable version of {@link detectedTheme} that automatically updates on changes, e.g. when the user
     * changes their operating system theme preference, or it switches depending on day/night.
     */
    get detectedTheme$() {
        if (typeof window === 'undefined') {
            return of('light');
        }
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        return fromEvent(mediaQuery, 'change').pipe(map(event => event.matches), startWith(mediaQuery.matches), map(matches => matches ? 'dark' : 'light'));
    }
    /**
     * @return the current theme
     */
    get theme() {
        return this._theme.getValue();
    }
    /**
     * @param value
     *  the new theme
     */
    set theme(value) {
        this._theme.next(value);
    }
}
ThemeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ThemeService, deps: [{ token: DOCUMENT }, { token: THEME_LOADER }, { token: THEME_SAVER }], target: i0.ɵɵFactoryTarget.Injectable });
ThemeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ThemeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ThemeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [THEME_LOADER]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [THEME_SAVER]
                }] }]; } });

/**
 * A simple boolean toggle for enabling dark mode.
 * Enabling the toggle sets the theme to `dark`, while disabling sets it to `light`.
 */
class DarkSwitchComponent {
    constructor(themeService) {
        this.themeService = themeService;
    }
    ngOnInit() {
        this.theme$ = this.themeService.theme$;
    }
}
DarkSwitchComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: DarkSwitchComponent, deps: [{ token: ThemeService }], target: i0.ɵɵFactoryTarget.Component });
DarkSwitchComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.1.1", type: DarkSwitchComponent, selector: "ng-bootstrap-darkmode-switch, ngbd-darkmode-switch", ngImport: i0, template: "<div class=\"custom-control custom-switch\">\n  <input type=\"checkbox\" class=\"custom-control-input\" id=\"darkModeSwitch\"\n         [ngModel]=\"(theme$ | async) === 'dark'\" (ngModelChange)=\"theme$.next($event ? 'dark' : 'light')\">\n  <label class=\"custom-control-label\" for=\"darkModeSwitch\">\n    Dark Mode\n  </label>\n</div>\n", styles: [""], directives: [{ type: i2.CheckboxControlValueAccessor, selector: "input[type=checkbox][formControlName],input[type=checkbox][formControl],input[type=checkbox][ngModel]" }, { type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }], pipes: { "async": i2$1.AsyncPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: DarkSwitchComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ng-bootstrap-darkmode-switch, ngbd-darkmode-switch',
                    templateUrl: './dark-switch.component.html',
                    styleUrls: ['./dark-switch.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: ThemeService }]; } });

/**
 * A tri-state toggle button for switching between `light`, `auto` and `dark` theme.
 * Size and style (icon vs label) are configurable.
 */
class ThemeSwitchComponent {
    constructor(themeService) {
        this.themeService = themeService;
    }
    ngOnInit() {
        this.theme$ = this.themeService.theme$;
    }
}
ThemeSwitchComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ThemeSwitchComponent, deps: [{ token: ThemeService }], target: i0.ɵɵFactoryTarget.Component });
ThemeSwitchComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.1.1", type: ThemeSwitchComponent, selector: "ng-bootstrap-theme-switch, ngbd-theme-switch", inputs: { size: "size", style: "style" }, ngImport: i0, template: "<div class=\"btn-group btn-group-toggle\"\n     [class.btn-group-sm]=\"size==='sm'\"\n     [class.btn-group-lg]=\"size==='lg'\"\n>\n  <button type=\"button\" class=\"btn btn-light\" (click)=\"theme$.next('light')\" [class.active]=\"(theme$ | async) === 'light'\">\n    <!-- https://primer.style/octicons/sun-16 -->\n    <svg *ngIf=\"style !== 'label'\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 16 16\" width=\"16\" height=\"16\" fill=\"black\"><path fill-rule=\"evenodd\" d=\"M8 10.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM8 12a4 4 0 100-8 4 4 0 000 8zM8 0a.75.75 0 01.75.75v1.5a.75.75 0 01-1.5 0V.75A.75.75 0 018 0zm0 13a.75.75 0 01.75.75v1.5a.75.75 0 01-1.5 0v-1.5A.75.75 0 018 13zM2.343 2.343a.75.75 0 011.061 0l1.06 1.061a.75.75 0 01-1.06 1.06l-1.06-1.06a.75.75 0 010-1.06zm9.193 9.193a.75.75 0 011.06 0l1.061 1.06a.75.75 0 01-1.06 1.061l-1.061-1.06a.75.75 0 010-1.061zM16 8a.75.75 0 01-.75.75h-1.5a.75.75 0 010-1.5h1.5A.75.75 0 0116 8zM3 8a.75.75 0 01-.75.75H.75a.75.75 0 010-1.5h1.5A.75.75 0 013 8zm10.657-5.657a.75.75 0 010 1.061l-1.061 1.06a.75.75 0 11-1.06-1.06l1.06-1.06a.75.75 0 011.06 0zm-9.193 9.193a.75.75 0 010 1.06l-1.06 1.061a.75.75 0 11-1.061-1.06l1.06-1.061a.75.75 0 011.061 0z\"></path></svg>\n    <span [class.sr-only]=\"style !== 'label'\">Light</span>\n  </button>\n  <button type=\"button\" class=\"btn btn-secondary\" (click)=\"theme$.next('auto')\">\n    <!-- https://primer.style/octicons/telescope-16 -->\n    <svg *ngIf=\"style !== 'label'\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 16 16\" width=\"16\" height=\"16\" fill=\"white\"><path fill-rule=\"evenodd\" d=\"M14.184 1.143a1.75 1.75 0 00-2.502-.57L.912 7.916a1.75 1.75 0 00-.53 2.32l.447.775a1.75 1.75 0 002.275.702l11.745-5.656a1.75 1.75 0 00.757-2.451l-1.422-2.464zm-1.657.669a.25.25 0 01.358.081l1.422 2.464a.25.25 0 01-.108.35l-2.016.97-1.505-2.605 1.85-1.26zM9.436 3.92l1.391 2.41-5.42 2.61-.942-1.63 4.97-3.39zM3.222 8.157l-1.466 1a.25.25 0 00-.075.33l.447.775a.25.25 0 00.325.1l1.598-.769-.83-1.436zm6.253 2.306a.75.75 0 00-.944-.252l-1.809.87a.75.75 0 00-.293.253L4.38 14.326a.75.75 0 101.238.848l1.881-2.75v2.826a.75.75 0 001.5 0v-2.826l1.881 2.75a.75.75 0 001.238-.848l-2.644-3.863z\"></path></svg>\n    <span [class.sr-only]=\"style !== 'label'\">Auto</span>\n  </button>\n  <button type=\"button\" class=\"btn btn-dark\" (click)=\"theme$.next('dark')\" [class.active]=\"(theme$ | async) === 'dark'\">\n    <!-- https://primer.style/octicons/moon-16 -->\n    <svg *ngIf=\"style !== 'label'\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 16 16\" width=\"16\" height=\"16\" fill=\"white\"><path fill-rule=\"evenodd\" d=\"M9.598 1.591a.75.75 0 01.785-.175 7 7 0 11-8.967 8.967.75.75 0 01.961-.96 5.5 5.5 0 007.046-7.046.75.75 0 01.175-.786zm1.616 1.945a7 7 0 01-7.678 7.678 5.5 5.5 0 107.678-7.678z\"></path></svg>\n    <span [class.sr-only]=\"style !== 'label'\">Dark</span>\n  </button>\n</div>\n", styles: [""], directives: [{ type: i2$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], pipes: { "async": i2$1.AsyncPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ThemeSwitchComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ng-bootstrap-theme-switch, ngbd-theme-switch',
                    templateUrl: './theme-switch.component.html',
                    styleUrls: ['./theme-switch.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: ThemeService }]; }, propDecorators: { size: [{
                type: Input
            }], style: [{
                type: Input
            }] } });

class NgBootstrapDarkmodeModule {
}
NgBootstrapDarkmodeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: NgBootstrapDarkmodeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NgBootstrapDarkmodeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: NgBootstrapDarkmodeModule, declarations: [DarkSwitchComponent,
        ThemeSwitchComponent], imports: [CommonModule,
        FormsModule], exports: [DarkSwitchComponent,
        ThemeSwitchComponent] });
NgBootstrapDarkmodeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: NgBootstrapDarkmodeModule, imports: [[
            CommonModule,
            FormsModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: NgBootstrapDarkmodeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DarkSwitchComponent,
                        ThemeSwitchComponent,
                    ],
                    imports: [
                        CommonModule,
                        FormsModule,
                    ],
                    exports: [
                        DarkSwitchComponent,
                        ThemeSwitchComponent,
                    ],
                }]
        }] });

/*
 * Public API Surface of ng-bootstrap-darkmode
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DarkSwitchComponent, NgBootstrapDarkmodeModule, THEME_LOADER, THEME_SAVER, ThemeService, ThemeSwitchComponent };
